import React from 'react';
import { LucideIcon } from 'lucide-react';
import { cn } from '../lib/utils';

interface VideoBackgroundProps {
  icon?: LucideIcon;
  title?: string;
  description?: string;
  showOverlay?: boolean;
  overlayPosition?: 'center' | 'top' | 'bottom';
  className?: string;
  theme?: 'light' | 'dark';
}

export function VideoBackground({
  icon: Icon,
  title,
  description,
  showOverlay = true,
  overlayPosition = 'center',
  className,
  theme = 'dark'
}: VideoBackgroundProps) {
  return (
    <div className={cn("relative w-full h-full overflow-hidden", className)}>
      {/* Subtle Animated Gradient Background */}
      <div className="absolute inset-0">
        {/* Primary animated gradient layer */}
        <div
          className="absolute opacity-0 gradient-animate-1"
          style={{
            top: '-10%',
            left: '-10%',
            right: '-10%',
            bottom: '-10%',
            background: theme === 'light' ? `
              transparent` : `
              radial-gradient(circle at 20% 30%, #136ef6 0%, transparent 50%),
              radial-gradient(circle at 70% 70%, #1e40af 0%, transparent 50%),
              radial-gradient(circle at 90% 10%, #3b82f6 0%, transparent 30%),
              radial-gradient(circle at 10% 80%, #0f172a 0%, transparent 40%)
            `
          }}
        />

        {/* Secondary overlay for depth */}
        <div
          className="absolute opacity-0 gradient-animate-2"
          style={{
            top: '-15%',
            left: '-15%',
            right: '-15%',
            bottom: '-15%',
            background: theme === 'light' ? `
             transparent
            ` : `
              radial-gradient(circle at 60% 40%, #1e293b 0%, transparent 60%),
              radial-gradient(circle at 30% 80%, #0f1419 0%, transparent 50%)
            `
          }}
        />

      </div>

      {/* Optional Content Overlay */}
      {showOverlay && (Icon || title || description) && (
        <div className={cn(
          "absolute inset-0 flex flex-col justify-center items-start text-left z-10",
          overlayPosition === 'top' && 'justify-start pt-16',
          overlayPosition === 'bottom' && 'justify-end pb-16'
        )}>
          <div className="max-w-3xl px-16">
            {Icon && (
              <div className="relative mb-6">
                <div className={`absolute inset-0 ${theme === 'light' ? 'bg-blue-400/30' : 'bg-blue-500/20'} rounded-full blur-xl`}></div>
                <div className={`relative ${theme === 'light' ? 'bg-white/80' : 'bg-blue-500/10'} backdrop-blur-sm p-4 rounded-full w-20 h-20 flex items-center justify-center border ${theme === 'light' ? 'border-blue-300/50' : 'border-blue-400/20'}`}>
                  <Icon className={`w-10 h-10 ${theme === 'light' ? 'text-blue-600' : 'text-white'}`} />
                </div>
              </div>
            )}

            {title && (
              <h1 className={`heading-xl ${theme === 'light' ? 'text-gray-900' : 'text-white'} mb-6 drop-shadow-lg text-left`}>
                {title}
              </h1>
            )}

            {description && (
              <p className={`body-lg ${theme === 'light' ? 'text-gray-700' : 'text-white/90'} max-w-2xl drop-shadow-md text-left`}>
                {description}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Subtle animated particles - hidden in light theme to show hero pattern */}
      {theme === 'dark' && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-1 h-1 bg-blue-400/60 rounded-full animate-pulse"
               style={{ animationDelay: '0s', animationDuration: '4s' }} />
          <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-blue-300/40 rounded-full animate-pulse"
               style={{ animationDelay: '2s', animationDuration: '6s' }} />
          <div className="absolute bottom-1/3 left-1/3 w-1 h-1 bg-blue-500/50 rounded-full animate-pulse"
               style={{ animationDelay: '4s', animationDuration: '5s' }} />
          <div className="absolute top-1/2 right-1/4 w-0.5 h-0.5 bg-blue-200/30 rounded-full animate-pulse"
               style={{ animationDelay: '1s', animationDuration: '7s' }} />
        </div>
      )}
    </div>
  );
}